import React from "react";
import { reportClientError } from "../lib/errorReporter";

/**
 * Top-level error boundary. Wraps the router so any thrown render error
 * shows a friendly recovery screen instead of a hard white screen —
 * critical on Capacitor mobile builds where WebInspector isn't available
 * out-of-the-box and users can't refresh the browser.
 *
 * Also POSTs the crash to /api/js-errors/report (v139.2) so the admin
 * dashboard can see production crashes without waiting for a user to
 * report them.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] render crashed:", error, info?.componentStack);
    reportClientError({
      message: error?.message || String(error),
      stack: (error?.stack || "") + (info?.componentStack ? "\n\n---\nComponent stack:" + info.componentStack : ""),
      kind: "boundary",
    });
  }

  reload = () => {
    this.setState({ hasError: false, error: null });
    try { window.location.reload(); } catch { /* noop */ }
  };

  goHome = () => {
    this.setState({ hasError: false, error: null });
    try { window.location.href = "/"; } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-page text-ink p-6 text-center pt-safe">
        <div className="w-16 h-16 bg-coral border-2 border-ink shadow-brutal flex items-center justify-center mb-6" aria-hidden>
          <span className="font-display font-black text-white text-3xl">!</span>
        </div>
        <h1 className="font-display font-black text-3xl tracking-tighter mb-2">Something went wrong.</h1>
        <p className="max-w-sm text-sm text-ink/70 mb-6">
          A page crashed unexpectedly. Reload the app or head back home to keep going.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button data-testid="errbnd-reload-btn" onClick={this.reload} className="btn-primary">Reload app</button>
          <button data-testid="errbnd-home-btn" onClick={this.goHome} className="btn-secondary">Go home</button>
        </div>
        {this.state.error?.message && (
          <p className="mt-6 max-w-sm text-[10px] font-mono text-ink/50 break-all">
            {String(this.state.error.message).slice(0, 240)}
          </p>
        )}
      </div>
    );
  }
}
